import { ArrowLeftToLine, ArrowRightToLine, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import i18nInstance from '@/i18n/config';
import { branding } from '@/config/branding';

interface SidebarHeaderProps {
  isCollapsed: boolean;
  shareConversationId?: string;
  onToggleCollapse: () => void;
  onNewConversation: () => void;
  onSelectedNavItem: (item: string) => void;
  onBackToChat: () => void;
}

export default function SidebarHeader({
  isCollapsed,
  shareConversationId,
  onToggleCollapse,
  onNewConversation,
  onSelectedNavItem,
  onBackToChat,
}: SidebarHeaderProps) {
  const { t } = useTranslation();
  const [, setForceUpdate] = useState(0);

  // 监听语言变化
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(n => n + 1);
    };
    i18nInstance.on('languageChanged', handleLanguageChange);
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange);
    };
  }, []);

  if (isCollapsed) {
    return (
      <div className="p-3 overflow-hidden">
        {/* Collapsed Logo */}
        <div className="flex justify-center mb-4">
          <img
            src={branding.collapsedLogo || branding.logo}
            alt={branding.name}
            style={{
              width: branding.sidebarCollapsedLogoStyle.width,
              height: branding.sidebarCollapsedLogoStyle.height,
            }}
            className={branding.sidebarCollapsedLogoStyle.className}
          />
        </div>
        {/* Expand Button */}
        <div className="flex justify-center mb-6">
          <button
            onClick={onToggleCollapse}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('sidebar.expand')}
          >
            <ArrowRightToLine size={16} />
          </button>
        </div>
        {/* Collapsed New Chat Button - disabled if sharing */}
        {!shareConversationId && (
          <button
            onClick={() => {
              onSelectedNavItem('');
              onBackToChat();
              onNewConversation();
            }}
            className="w-full flex justify-center p-2 text-sm transition-all duration-200 mb-4 rounded-lg group"
            style={{ color: branding.accentColor }}
            onMouseEnter={e =>
              (e.currentTarget.style.backgroundColor =
                branding.accentColorHover)
            }
            onMouseLeave={e =>
              (e.currentTarget.style.backgroundColor = 'transparent')
            }
            title={t('tooltips.newChat')}
          >
            <div
              className="text-white rounded-full p-1 transition-transform duration-200 group-hover:scale-110"
              style={{ backgroundColor: branding.accentColor }}
            >
              <Plus size={18} className="transition-all duration-200" />
            </div>
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="relative top-0 left-0 right-0 overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 min-w-0">
          <img
            src={branding.logo}
            alt={branding.name}
            style={{
              width: branding.sidebarExpandedLogoStyle.width,
              height: branding.sidebarExpandedLogoStyle.height,
              flexShrink: 0,
            }}
            className={branding.sidebarExpandedLogoStyle.className}
          />
          {branding.showNameInSidebar && (
            <span
              className="font-semibold text-gray-800 whitespace-nowrap"
              style={branding.sidebarTextStyle}
            >
              {branding.sidebarText || branding.name}
            </span>
          )}
        </div>
        {/* Collapse Button */}
        <button
          onClick={onToggleCollapse}
          className="hidden sm:block p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          title={t('sidebar.collapse')}
        >
          <ArrowLeftToLine size={16} />
        </button>
      </div>
      {/* New Chat Button - disabled if sharing, hidden on mobile (bottom tab bar has +) */}
      {!shareConversationId && (
        <button
          onClick={() => {
            onSelectedNavItem('');
            onBackToChat();
            onNewConversation();
          }}
          className="w-full hidden sm:flex items-center gap-3 px-2 py-2 text-sm transition-all duration-200 rounded-lg group overflow-hidden whitespace-nowrap"
          style={{ color: branding.accentColor }}
          onMouseEnter={e =>
            (e.currentTarget.style.backgroundColor = branding.accentColorHover)
          }
          onMouseLeave={e =>
            (e.currentTarget.style.backgroundColor = 'transparent')
          }
        >
          <div
            className="text-white rounded-full p-1 transition-transform duration-200 group-hover:scale-110"
            style={{ backgroundColor: branding.accentColor }}
          >
            <Plus size={18} className="transition-all duration-200" />
          </div>
          <span className="font-medium">{t('sidebar.newChat')}</span>
        </button>
      )}
    </div>
  );
}
